import type { ReactNode } from "react";
import { RecordRepresentation, useRecordContext, useTranslate } from "ra-core";
import { ReferenceField } from "@/components/admin/reference-field";
import { TextField } from "@/components/admin/text-field";
import { Badge } from "@/components/ui/badge";

import type { Contact } from "../types";
import { CONTACT_TYPE_LABEL_KEYS, typesRequireExtra } from "./contactTypes";

const InfoRow = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <div className="flex flex-col gap-0.5 py-1">
    <span className="text-sm text-muted-foreground">{label}</span>
    <span className="text-base md:text-sm">{children}</span>
  </div>
);

const ContactLink = ({
  source,
  label,
}: {
  source: keyof Contact;
  label: string;
}) => (
  <InfoRow label={label}>
    <ReferenceField source={source as string} reference="contacts" link="show">
      <RecordRepresentation />
    </ReferenceField>
  </InfoRow>
);

/**
 * Almikia Epic EC2a: the read view of a contact's classification: type chips, origin (with
 * the referring contact or colega as a link), business unit and project, and the colega /
 * supplier / aval details when the matching type is present.
 */
export const ContactClassificationInfo = () => {
  const record = useRecordContext<Contact>();
  const translate = useTranslate();
  if (!record) return null;
  const types = record.contact_types ?? [];

  return (
    <div className="flex flex-col">
      {types.length > 0 && (
        <div className="flex flex-wrap gap-1 pb-2">
          {types.map((type) => (
            <Badge key={type} variant="secondary">
              {translate(CONTACT_TYPE_LABEL_KEYS[type])}
            </Badge>
          ))}
        </div>
      )}
      {record.lead_source_id != null && (
        <InfoRow label={translate("resources.contacts.fields.lead_source_id")}>
          <ReferenceField
            source="lead_source_id"
            reference="lead_sources"
            link={false}
          >
            <TextField source="name" />
          </ReferenceField>
        </InfoRow>
      )}
      {record.referred_by_contact_id != null && (
        <ContactLink
          source="referred_by_contact_id"
          label={translate("resources.contacts.fields.referred_by_contact_id")}
        />
      )}
      {record.origin_colega_contact_id != null && (
        <ContactLink
          source="origin_colega_contact_id"
          label={translate(
            "resources.contacts.fields.origin_colega_contact_id",
          )}
        />
      )}
      {record.business_unit != null && (
        <InfoRow label={translate("resources.contacts.fields.business_unit")}>
          {translate(
            `resources.contacts.business_units.${record.business_unit}`,
          )}
        </InfoRow>
      )}
      {record.business_project_id != null && (
        <InfoRow
          label={translate("resources.contacts.fields.business_project_id")}
        >
          <ReferenceField
            source="business_project_id"
            reference="projects"
            link={false}
          >
            <TextField source="name" />
          </ReferenceField>
        </InfoRow>
      )}
      {typesRequireExtra(types, "colega") && (
        <>
          {record.colega_kind != null && (
            <InfoRow label={translate("resources.contacts.fields.colega_kind")}>
              {translate(
                `resources.contacts.colega_kinds.${record.colega_kind}`,
              )}
            </InfoRow>
          )}
          {record.colega_affiliation != null && (
            <InfoRow
              label={translate("resources.contacts.fields.colega_affiliation")}
            >
              {translate(
                `resources.contacts.colega_affiliations.${record.colega_affiliation}`,
              )}
            </InfoRow>
          )}
          {record.colega_agreement_signed != null && (
            <InfoRow
              label={translate(
                "resources.contacts.fields.colega_agreement_signed",
              )}
            >
              {translate(
                record.colega_agreement_signed
                  ? "resources.contacts.agreement.yes"
                  : "resources.contacts.agreement.no",
              )}
            </InfoRow>
          )}
        </>
      )}
      {typesRequireExtra(types, "supplier") &&
        record.supplier_category_id != null && (
          <InfoRow
            label={translate("resources.contacts.fields.supplier_category_id")}
          >
            <ReferenceField
              source="supplier_category_id"
              reference="supplier_categories"
              link={false}
            >
              <TextField source="name" />
            </ReferenceField>
          </InfoRow>
        )}
      {typesRequireExtra(types, "guarantor") &&
        record.guarantor_of_contact_id != null && (
          <ContactLink
            source="guarantor_of_contact_id"
            label={translate(
              "resources.contacts.fields.guarantor_of_contact_id",
            )}
          />
        )}
    </div>
  );
};
